import React, { useState, useRef, useEffect, useCallback } from "react";
import { format, addMonths, subDays } from "date-fns";
import { SYM } from "../utils/textSymbols";
import "./SmartAddModal.css";

/* --- helpers --- */

function formatTime12(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? "pm" : "am";
  return `${hr % 12 || 12}:${m} ${ampm}`;
}

/* --- EditDetailModal --- */

function EditDetailModal({ event, participants: allParticipants, onSave, onCancel }) {
  const [form, setForm] = useState({ ...event });

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const toggleParticipant = (name) => {
    setForm((p) => ({
      ...p,
      participants: p.participants.includes(name)
        ? p.participants.filter((n) => n !== name)
        : [...p.participants, name],
    }));
  };

  return (
    <div className="sa-overlay sa-detail-overlay" onClick={onCancel}>
      <div className="sa-detail-modal" onClick={(e) => e.stopPropagation()}>
        <header className="sa-detail-header">
          <button className="sa-close-btn" onClick={onCancel} aria-label="Close">{SYM.close}</button>
          <h3>Edit Event</h3>
          <button className="sa-save-btn" onClick={() => onSave(form)}>Save</button>
        </header>

        <div className="sa-detail-body">
          <input
            className="sa-input sa-input-title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Event title"
          />
          <input
            className="sa-input"
            value={form.location || ""}
            onChange={(e) => set("location", e.target.value)}
            placeholder="Location (optional)"
          />

          <div className="sa-detail-section">
            <span className="sa-detail-label">Who's going</span>
            <div className="sa-chips">
              {allParticipants.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`sa-chip ${form.participants?.includes(name) ? "sa-chip-on" : ""}`}
                  onClick={() => toggleParticipant(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          <label className="sa-toggle-row">
            <input
              type="checkbox"
              checked={form.allDay || false}
              onChange={(e) => set("allDay", e.target.checked)}
            />
            <span>All day</span>
          </label>

          <div className="sa-datetime-grid">
            <div className="sa-datetime-group">
              <span className="sa-datetime-label">Start</span>
              <input type="date" className="sa-input sa-input-date" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} />
              {!form.allDay && <input type="time" className="sa-input sa-input-time" value={form.startTime} onChange={(e) => set("startTime", e.target.value)} />}
            </div>
            <div className="sa-datetime-group">
              <span className="sa-datetime-label">End</span>
              <input type="date" className="sa-input sa-input-date" value={form.endDate} onChange={(e) => set("endDate", e.target.value)} />
              {!form.allDay && <input type="time" className="sa-input sa-input-time" value={form.endTime} onChange={(e) => set("endTime", e.target.value)} />}
            </div>
          </div>

          <textarea
            className="sa-input sa-textarea"
            value={form.notes || ""}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Notes (optional)"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}

/* --- SmartAddModal --- */

export default function SmartAddModal({ mode, participants, onCreateEvent, onClose, apiBase, promptContext }) {
  // phases: "input" | "loading" | "review"
  const [phase, setPhase] = useState("input");
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [editingIdx, setEditingIdx] = useState(null);
  const [approving, setApproving] = useState(new Set());
  const [approved, setApproved] = useState(new Set());
  const [approvingAll, setApprovingAll] = useState(false);
  const [schoolEnabled] = useState(mode === "school");
  const [schoolSettings, setSchoolSettings] = useState(() => ({
    child: "", school: "", schoolAliases: "", schoolWebsite: "", from: format(new Date(), "yyyy-MM-dd"),
    to: format(subDays(addMonths(new Date(), 3), 1), "yyyy-MM-dd"), cycleLength: 6, startingDay: 1,
  }));
  const [schoolReview, setSchoolReview] = useState(null);
  const [closureKeys, setClosureKeys] = useState([]);
  const [closuresDirty, setClosuresDirty] = useState(false);
  const [closuresReviewed, setClosuresReviewed] = useState(false);
  const [schoolProfiles, setSchoolProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [savedPlan, setSavedPlan] = useState(null);
  const [profileStatus, setProfileStatus] = useState("");

  /* -- Chat state -- */
  const [chatText, setChatText] = useState("");

  /* -- Camera state -- */
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [imageContext, setImageContext] = useState("");

  /* -- Voice state -- */
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const participantNames = (participants || []).map((p) => (typeof p === "object" ? p.name : p));

  /* -- cleanup on unmount -- */
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_) { /* noop */ }
      }
    };
  }, []);

  useEffect(() => {
    if (mode !== "school") return;
    fetch(`${apiBase}/api/GetSchoolProfiles`, { credentials: "include" })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error(`Could not load school schedules (${res.status})`)))
      .then((data) => setSchoolProfiles(data.profiles || []))
      .catch((err) => setError(err.message));
  }, [apiBase, mode]);

  /* -- auto-start camera / voice -- */
  useEffect(() => {
    if (mode === "camera" && phase === "input" && !capturedImage) startCamera();
    if (mode === "voice" && phase === "input") startListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -- API call -- */
  const submitToGrok = useCallback(async (type, content, opts = {}) => {
    if (schoolEnabled && !schoolSettings.child) {
      setError("Choose a child for the school schedule.");
      return;
    }
    setPhase("loading");
    setError(null);
    try {
      const payload = { type, content, familyMembers: participantNames };
      if (schoolEnabled) payload.schoolSchedule = schoolSettings;
      if (schoolEnabled && selectedProfileId) payload.schoolProfileId = selectedProfileId;
      if (opts.schoolPlan) {
        payload.schoolPlan = opts.schoolPlan;
        payload.schoolClosureKeys = opts.schoolClosureKeys;
      }
      const globalCtx = typeof promptContext === "string" ? promptContext.trim() : "";
      const imageCtx = type === "image" && typeof opts.imageContext === "string" ? opts.imageContext.trim() : "";
      const ctxParts = [];
      if (globalCtx) ctxParts.push(globalCtx);
      if (imageCtx) ctxParts.push(`About this image: ${imageCtx}`);
      if (ctxParts.length) payload.promptContext = ctxParts.join("\n\n");
      const res = await fetch(`${apiBase}/api/SmartAdd`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail ? `\n${errData.detail}` : "";
        throw new Error((errData.error || `API error ${res.status}`) + detail);
      }
      const data = await res.json();
      const parsed = (data.events || []).map((ev, i) => {
        const rawParticipants = Array.isArray(ev.participants) ? ev.participants : [];
        // Normalize "All"/"all" to empty array so create payload sends no members (family default)
        const participants = rawParticipants.filter((p) => p !== "All" && p !== "all");
        return {
          _idx: i,
          title: ev.title || "(No title)",
          startDate: ev.startDate || format(new Date(), "yyyy-MM-dd"),
          startTime: ev.startTime || "09:00",
          endDate: ev.endDate || ev.startDate || format(new Date(), "yyyy-MM-dd"),
          endTime: ev.endTime || "10:00",
          allDay: !!ev.allDay,
          location: ev.location || "",
          notes: ev.notes || "",
          participants,
          recurrence: ev.recurrence || null,
          cycleDay: ev.cycleDay,
        };
      });
      if (parsed.length === 0 && !data.schoolReview) {
        setError("No events found. Try rephrasing your input.");
        setPhase("input");
        return;
      }
      setEvents(parsed);
      setApproved(new Set());
      setSchoolReview(data.schoolReview || null);
      setClosureKeys(data.schoolReview?.plan?.closureKeys || []);
      setClosuresDirty(false);
      setClosuresReviewed(false);
      setPhase("review");
    } catch (err) {
      setError(err.message);
      setPhase(opts.schoolPlan ? "review" : "input");
    }
  }, [apiBase, participantNames, promptContext, schoolEnabled, schoolSettings, selectedProfileId]);

  /* -- Chat handlers -- */
  const handleChatSend = () => {
    const text = chatText.trim();
    if (!text) return;
    submitToGrok("text", text);
  };

  /* -- Camera handlers -- */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 100);
    } catch {
      setError("Could not access camera. Try uploading a file instead.");
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setCapturedImage(dataUrl);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCapturedImage(ev.target.result);
    reader.readAsDataURL(file);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  const handleImageSubmit = () => {
    if (!capturedImage) return;
    submitToGrok("image", capturedImage, { imageContext });
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  /* -- Voice handlers -- */
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("Speech recognition is not supported in this browser. Try Chrome or Edge.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    let finalTranscript = "";
    recognition.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          finalTranscript += e.results[i][0].transcript + " ";
        } else {
          interim += e.results[i][0].transcript;
        }
      }
      setVoiceTranscript(finalTranscript + interim);
    };
    recognition.onerror = (e) => {
      if (e.error !== "aborted") setError("Speech recognition error: " + e.error);
      setListening(false);
    };
    recognition.onend = () => setListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
    setVoiceTranscript("");
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  };

  const handleVoiceSend = () => {
    const text = voiceTranscript.trim();
    if (!text) return;
    stopListening();
    submitToGrok("text", text);
  };

  /* -- Review handlers -- */
  const handleDeleteEvent = (idx) => {
    setEvents((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleEditSave = (updated) => {
    setEvents((prev) => prev.map((ev, i) => (i === editingIdx ? { ...ev, ...updated } : ev)));
    setEditingIdx(null);
  };

  const handleApproveOne = async (idx, deferRefresh = false) => {
    const ev = events[idx];
    setApproving((p) => new Set(p).add(idx));
    try {
      await onCreateEvent({
        id: `new-${Date.now()}-${idx}`,
        title: ev.title,
        startDate: ev.startDate,
        startTime: ev.allDay ? "00:00" : ev.startTime,
        endDate: ev.endDate,
        endTime: ev.allDay ? "00:00" : ev.endTime,
        allDay: ev.allDay,
        location: ev.location,
        notes: ev.notes,
        selectedParticipants: ev.participants,
        recurrence: ev.recurrence,
        reminders: Array.isArray(ev.reminders) ? ev.reminders : undefined,
      }, { deferRefresh });
      setApproved((p) => new Set(p).add(idx));
      return true;
    } catch (err) {
      setError(`Failed to create "${ev.title}": ${err.message}`);
      return false;
    } finally {
      setApproving((p) => { const n = new Set(p); n.delete(idx); return n; });
    }
  };

  const handleApproveAll = async () => {
    setApprovingAll(true);
    setError(null);
    let success = true;
    if (schoolReview && !await saveProfile()) success = false;
    for (let i = 0; i < events.length; i++) {
      if (!success) break;
      if (approved.has(i)) continue;
      if (!await handleApproveOne(i, true)) { success = false; break; }
    }
    setApprovingAll(false);
    if (success) onClose();
  };

  const unapprovedCount = events.filter((_, i) => !approved.has(i)).length;
  const saving = approvingAll || approving.size > 0;
  const canSave = !saving && (!schoolReview || (!closuresDirty && closuresReviewed));
  const setSchool = (field, value) => setSchoolSettings((prev) => ({ ...prev, [field]: value }));
  const selectedSchoolProfile = schoolProfiles.find((item) => item.id === selectedProfileId);
  const chooseProfile = (id) => {
    setSelectedProfileId(id);
    const profile = schoolProfiles.find((item) => item.id === id);
    if (!profile) { setSavedPlan(null); return; }
    setSchoolSettings((prev) => ({ ...prev, child: profile.child, school: profile.school, schoolAliases: profile.schoolAliases || "", schoolWebsite: profile.schoolWebsite || "", cycleLength: profile.cycleLength, startingDay: Math.min(prev.startingDay, profile.cycleLength) }));
    setSavedPlan({ templates: profile.templates, closureKeys: [] });
  };
  async function saveProfile() {
    if (!schoolReview) return;
    setProfileStatus("Saving…");
    try {
      const res = await fetch(`${apiBase}/api/SaveSchoolProfile`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ profile: { id: selectedProfileId || undefined, ...schoolReview.settings, templates: schoolReview.plan.templates } }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setSelectedProfileId(data.profile.id); setSavedPlan({ templates: data.profile.templates, closureKeys: [] });
      setSchoolProfiles((prev) => [...prev.filter((item) => item.id !== data.profile.id), data.profile].sort((a, b) => a.child.localeCompare(b.child)));
      setProfileStatus("Schedule saved for future date ranges.");
      return data.profile;
    } catch (err) { setProfileStatus(err.message); return null; }
  }

  const renderSchoolOptions = () => (
    <section className="sa-school-options">
      <strong>School schedule</strong>
      <p className="sa-phase-hint">Reuse a saved timetable or create a new one. Smart Add checks your calendar and pauses the rotation on closures.</p>
      <label className="sa-school-profile-picker">Saved schedule<select className="sa-input" value={selectedProfileId} onChange={(e) => chooseProfile(e.target.value)}><option value="">Create a new schedule</option>{schoolProfiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.child} — {profile.school}</option>)}</select></label>
      {selectedSchoolProfile && <div className="sa-school-reference">
        <strong>{selectedSchoolProfile.child}&apos;s saved rotation</strong>
        <ul>{Array.from({ length: selectedSchoolProfile.cycleLength }, (_, index) => {
          const day = index + 1;
          const extras = (selectedSchoolProfile.templates || []).filter((item) => item.cycleDay === day && item.title.toLowerCase() !== `day ${day}`).map((item) => item.title);
          const description = selectedSchoolProfile.dayDescriptions?.[day] || (selectedSchoolProfile.templates || []).find((item) => item.cycleDay === day && item.title.toLowerCase() === `day ${day}`)?.notes || extras.join(", ");
          return <li key={day}><strong>Day {day}</strong>{description ? ` — ${description}` : ""}</li>;
        })}</ul>
      </div>}
        <div className="sa-school-grid">
          <label className="sa-school-field"><span className="sa-school-field-label">Child</span><select className="sa-input" value={schoolSettings.child} onChange={(e) => setSchool("child", e.target.value)}>
            <option value="">Choose child</option>
            {participantNames.filter((name) => name && name.toLowerCase() !== "all").map((name) => <option key={name}>{name}</option>)}
          </select></label>
          <label className="sa-school-field"><span className="sa-school-field-label">School <small>Helps match closures</small></span><input className="sa-input" value={schoolSettings.school} onChange={(e) => setSchool("school", e.target.value)} placeholder="School name" /></label>
          <label className="sa-school-field"><span className="sa-school-field-label">School aliases <small>Comma separated</small></span><input className="sa-input" value={schoolSettings.schoolAliases} onChange={(e) => setSchool("schoolAliases", e.target.value)} placeholder="District, abbreviation" /></label>
          <label className="sa-school-field"><span className="sa-school-field-label">School website <small>For future monitoring</small></span><input type="url" className="sa-input" value={schoolSettings.schoolWebsite} onChange={(e) => setSchool("schoolWebsite", e.target.value)} placeholder="https://…" /></label>
          <label className="sa-school-field"><span className="sa-school-field-label">From</span><input type="date" className="sa-input" value={schoolSettings.from} onChange={(e) => setSchool("from", e.target.value)} /></label>
          <label className="sa-school-field"><span className="sa-school-field-label">Through</span><input type="date" className="sa-input" value={schoolSettings.to} onChange={(e) => setSchool("to", e.target.value)} /></label>
          <label className="sa-school-field"><span className="sa-school-field-label">Days in rotation</span><select className="sa-input" value={schoolSettings.cycleLength} onChange={(e) => {
            const cycleLength = Number(e.target.value);
            setSchoolSettings((prev) => ({ ...prev, cycleLength, startingDay: Math.min(prev.startingDay, cycleLength) }));
          }}>
            {Array.from({ length: 7 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1} day{i ? "s" : ""}</option>)}
          </select></label>
          <label className="sa-school-field"><span className="sa-school-field-label">First open day is</span><select className="sa-input" value={schoolSettings.startingDay} onChange={(e) => setSchool("startingDay", Number(e.target.value))}>
            {Array.from({ length: schoolSettings.cycleLength }, (_, i) => <option key={i} value={i + 1}>Day {i + 1}</option>)}
          </select></label>
        </div>
        <p className="sa-phase-hint">The days repeat in order and pause on closures. Example: “Add Day 1–{schoolSettings.cycleLength} labels” or describe what happens on each day.</p>
    </section>
  );

  const renderSchoolReview = () => schoolReview && (
    <section className="sa-school-options">
      <strong>{schoolReview.settings.child}: {schoolReview.settings.from} through {schoolReview.settings.to}</strong>
      <p>{schoolReview.days.length} open weekdays · {schoolReview.skippedDays.length} weekdays skipped · {(schoolReview.existingDays || []).length} existing entries kept · Next batch starts on Day {schoolReview.nextDay}</p>
      <p className="sa-phase-hint">Check the selected closures, especially if your children attend different schools. Half-days count as school days unless you select them here.</p>
      {schoolReview.closureWarning && <p className="sa-school-warning">{schoolReview.closureWarning}</p>}
      <button type="button" className="sa-retake-btn" onClick={saveProfile} disabled={saving}>Save this timetable for future ranges</button>
      {profileStatus && <p className="sa-phase-hint">{profileStatus}</p>}
      <details open>
        <summary>Calendar closures ({closureKeys.length} selected)</summary>
        {schoolReview.calendar.filter((ev) => closureKeys.includes(ev.key)).map((ev) => (
          <p key={ev.key} className="sa-school-closure">{ev.from}{ev.to !== ev.from ? ` – ${ev.to}` : ""}: {ev.title}</p>
        ))}
        {!closureKeys.length && <p>No closures selected. Check the calendar events below before saving.</p>}
      </details>
      <details>
        <summary>Choose which calendar events mean school is closed</summary>
        <div className="sa-school-calendar">
          {schoolReview.calendar.map((ev) => <label key={ev.key} className="sa-toggle-row">
            <input type="checkbox" checked={closureKeys.includes(ev.key)} disabled={saving || approved.size > 0} onChange={(e) => {
              setClosureKeys((prev) => e.target.checked ? [...prev, ev.key] : prev.filter((key) => key !== ev.key));
              setClosuresDirty(true); setClosuresReviewed(false);
            }} />
            <span>{ev.from}{ev.to !== ev.from ? ` – ${ev.to}` : ""}: {ev.title}<small>{ev.participants.join(", ")}</small></span>
          </label>)}
          {!schoolReview.calendar.length && <p>No calendar events found in this period.</p>}
        </div>
      </details>
      {closuresDirty && <button className="sa-submit-btn" onClick={() => submitToGrok("text", "", { schoolPlan: schoolReview.plan, schoolClosureKeys: closureKeys })}>Update schedule preview</button>}
      <details><summary>Rotation dates and skipped days</summary>
        <div className="sa-school-calendar">
          {schoolReview.days.map((day) => <p key={day.date}>{day.date}: Day {day.cycleDay}</p>)}
          {schoolReview.skippedDays.map((day) => <p key={day.date}>{day.date}: Closed — {day.titles.join(", ")}</p>)}
        </div>
      </details>
      <label className="sa-toggle-row"><input type="checkbox" checked={closuresReviewed} disabled={closuresDirty || saving || approved.size > 0} onChange={(e) => setClosuresReviewed(e.target.checked)} /><span>I reviewed the closures and rotation dates</span></label>
      <p className="sa-phase-hint">Saves individual dated events. Later calendar changes will not move this batch automatically. Use a non-overlapping period for your next batch.</p>
    </section>
  );

  /* -- render phases -- */

  const renderInput = () => {
    if (mode === "school") {
      return <div className="sa-input-phase">
        <h3 className="sa-phase-title">Timetable</h3>
        {savedPlan ? <p className="sa-phase-hint">The saved timetable is ready. Choose the range and starting day above.</p> : <textarea className="sa-chat-input" value={chatText} onChange={(e) => setChatText(e.target.value)} placeholder="Describe each rotation day, or ask for day labels only…" rows={4} />}
        {!savedPlan && <p className="sa-phase-hint">Have a printed timetable, PDF screenshot, or photo? Upload an image and the AI will read which classes or activities belong to each rotation day.</p>}
        <div className="sa-preview-actions">
          {!savedPlan && <><button type="button" className="sa-file-btn sa-row-action-btn" onClick={() => fileInputRef.current?.click()}>Upload timetable photo or screenshot</button><input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} /></>}
          <button className="sa-submit-btn sa-row-action-btn" onClick={() => savedPlan ? submitToGrok("text", "", { schoolPlan: savedPlan }) : capturedImage ? submitToGrok("image", capturedImage) : handleChatSend()} disabled={!savedPlan && !capturedImage && !chatText.trim()}>Build schedule</button>
        </div>
        {capturedImage && !savedPlan && <div className="sa-preview-area"><img src={capturedImage} alt="Timetable" className="sa-preview-img" /></div>}
        {error && <p className="sa-error">{error}</p>}
      </div>;
    }
    if (mode === "chat") {
      return (
        <div className="sa-input-phase">
          <h3 className="sa-phase-title">Describe your event</h3>
          <p className="sa-phase-hint">e.g. "Soccer practice every Tuesday at 4pm for Nora starting next week"</p>
          <textarea
            className="sa-chat-input"
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            placeholder="Type here..."
            rows={4}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
          />
          {error && <p className="sa-error">{error}</p>}
          <button className="sa-submit-btn" onClick={handleChatSend} disabled={!chatText.trim()}>
            Send to AI
          </button>
        </div>
      );
    }

    if (mode === "camera") {
      return (
        <div className="sa-input-phase">
          <h3 className="sa-phase-title">Capture or upload an image</h3>
          <p className="sa-phase-hint">Take a photo of a schedule, flyer, or event details</p>

          {!capturedImage ? (
            <div className="sa-camera-area">
              {cameraActive && (
                <video ref={videoRef} autoPlay playsInline muted className="sa-camera-video" />
              )}
              <div className="sa-camera-controls">
                {cameraActive && (
                  <button type="button" className="sa-capture-btn" onClick={capturePhoto} aria-label="Take photo">
                    <div className="sa-capture-ring" />
                  </button>
                )}
                <button type="button" className="sa-file-btn sa-camera-row-btn" onClick={() => fileInputRef.current?.click()}>
                  Choose File
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} />
              </div>
            </div>
          ) : (
            <div className="sa-preview-area">
              <img src={capturedImage} alt="Captured" className="sa-preview-img" />
            </div>
          )}
          <label className="sa-image-context-label" htmlFor="sa-image-context">Context for the AI (optional)</label>
          <textarea
            id="sa-image-context"
            className="sa-image-context-input"
            value={imageContext}
            onChange={(e) => setImageContext(e.target.value)}
            placeholder={`e.g. Events are for Nora only; skip March 10${SYM.enDash}14 (spring break); this is a lunch menu for February`}
            rows={3}
            disabled={phase !== "input"}
          />
          {capturedImage && (
            <div className="sa-preview-actions">
              <button type="button" className="sa-retake-btn sa-row-action-btn" onClick={retakePhoto}>Retake</button>
              <button type="button" className="sa-submit-btn sa-row-action-btn" onClick={handleImageSubmit}>Send to AI</button>
            </div>
          )}
          {error && <p className="sa-error">{error}</p>}
        </div>
      );
    }

    if (mode === "voice") {
      return (
        <div className="sa-input-phase">
          <h3 className="sa-phase-title">Speak your event details</h3>
          <p className="sa-phase-hint">e.g. "Add a dentist appointment for Andy next Thursday at 2pm"</p>

          <div className="sa-voice-area">
            <button
              className={`sa-mic-btn ${listening ? "sa-mic-listening" : ""}`}
              onClick={listening ? stopListening : startListening}
              aria-label={listening ? "Stop recording" : "Start recording"}
            >
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="1" width="6" height="12" rx="3" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
            {listening && <span className="sa-listening-label">Listening...</span>}
          </div>

          {voiceTranscript && (
            <div className="sa-transcript">
              <span className="sa-transcript-label">Transcript:</span>
              <p>{voiceTranscript}</p>
            </div>
          )}

          {error && <p className="sa-error">{error}</p>}
          <button className="sa-submit-btn" onClick={handleVoiceSend} disabled={!voiceTranscript.trim()}>
            Send to AI
          </button>
        </div>
      );
    }

    return null;
  };

  const renderLoading = () => (
    <div className="sa-loading-phase">
      <div className="sa-spinner-large" />
      <p className="sa-loading-text">{schoolEnabled ? "Checking the calendar and building school days..." : "Analyzing..."}</p>
    </div>
  );

  const renderReview = () => (
    <div className="sa-review-phase">
      <h3 className="sa-phase-title">
        {events.length} event{events.length !== 1 ? "s" : ""} found
      </h3>
      {error && <p className="sa-error">{error}</p>}

      {renderSchoolReview()}
      {approved.size === 0 && <button className="sa-retake-btn" disabled={saving} onClick={() => { setPhase("input"); setSchoolReview(null); }}>Back to input</button>}

      <div className="sa-event-list">
        {events.map((ev, i) => (
          <div key={i} className={`sa-event-row ${approved.has(i) ? "sa-event-approved" : ""}`}>
            <div className="sa-event-info">
              <span className="sa-event-title">{ev.title}</span>
              <span className="sa-event-meta">
                {ev.startDate} {!ev.allDay && formatTime12(ev.startTime)}
                {ev.cycleDay && ` · Day ${ev.cycleDay}`}
                {!ev.allDay && ev.endTime && ` - ${formatTime12(ev.endTime)}`}
              </span>
              {ev.participants?.length > 0 && (
                <span className="sa-event-attendees">{ev.participants.join(", ")}</span>
              )}
            </div>
            <div className="sa-event-actions">
              {!approved.has(i) && (
                <>
                  <button className="sa-action-btn sa-edit-btn" disabled={saving || approved.size > 0} onClick={() => setEditingIdx(i)} title="Edit" aria-label="Edit">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button className="sa-action-btn sa-delete-btn" disabled={saving || approved.size > 0} onClick={() => handleDeleteEvent(i)} title="Delete" aria-label="Delete">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                  <button
                    className="sa-action-btn sa-approve-btn"
                    onClick={() => handleApproveOne(i)}
                    disabled={!canSave}
                    title="Approve"
                    aria-label="Approve"
                  >
                    {approving.has(i) ? <span className="sa-spinner-sm" /> : (
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    )}
                  </button>
                </>
              )}
              {approved.has(i) && <span className="sa-approved-badge">Created</span>}
            </div>
          </div>
        ))}
      </div>

      {unapprovedCount > 0 && (
        <button className="sa-approve-all-btn" onClick={handleApproveAll} disabled={!canSave}>
          {approvingAll ? <><span className="sa-spinner-sm" /> Creating... {approved.size} of {events.length} saved</> : `Approve All (${unapprovedCount})`}
        </button>
      )}
      {unapprovedCount === 0 && (
        <button className="sa-done-btn" onClick={onClose}>Done</button>
      )}
    </div>
  );

  return (
    <div className="sa-overlay" onClick={() => { if (!saving) onClose(); }}>
      <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
        <header className="sa-header">
          <button className="sa-close-btn" disabled={saving} onClick={onClose} aria-label="Close">{SYM.close}</button>
          <h2 className="sa-modal-title">
            {mode === "chat" && `Smart Add ${SYM.emDash} Chat`}
            {mode === "camera" && `Smart Add ${SYM.emDash} Camera`}
            {mode === "voice" && `Smart Add ${SYM.emDash} Voice`}
            {mode === "school" && "School Schedules"}
          </h2>
        </header>

        <div className="sa-body">
          {phase === "input" && mode === "school" && renderSchoolOptions()}
          {phase === "input" && renderInput()}
          {phase === "loading" && renderLoading()}
          {phase === "review" && renderReview()}
        </div>
      </div>

      {editingIdx !== null && (
        <EditDetailModal
          event={events[editingIdx]}
          participants={participantNames}
          onSave={handleEditSave}
          onCancel={() => setEditingIdx(null)}
        />
      )}
    </div>
  );
}
